import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Clock, User, Tag, AlertCircle, MessageSquare, Send, Loader2, Paperclip, Download, Trash2, File as FileIcon, Monitor, Link as LinkIcon, Sparkles, Lock, Mail, Building2, HardDrive, CheckCircle, MailIcon, Plus, X } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import MDEditor from '@uiw/react-md-editor';
import TicketTimeTracker from '../components/TicketTimeTracker';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [asset, setAsset] = useState(null);
  const [client, setClient] = useState(null);
  const [previousTickets, setPreviousTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendEmailOnReply, setSendEmailOnReply] = useState(true);
  const [showInternalNotes, setShowInternalNotes] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolving, setResolving] = useState(false);
  const [ccEmails, setCcEmails] = useState([]);
  const [newCcEmail, setNewCcEmail] = useState('');
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => {
    fetchTicketData();
  }, [id]);

  const fetchTicketData = async () => {
    setLoading(true);
    try {
      const [ticketRes, commentsRes, attachmentsRes] = await Promise.all([
        api.get(`/tickets/${id}`),
        api.get(`/tickets/${id}/comments`),
        api.get(`/tickets/${id}/attachments`)
      ]);
      setTicket(ticketRes.data);
      setComments(commentsRes.data.comments || []);
      setAttachments(attachmentsRes.data.attachments || []);
      setCcEmails(ticketRes.data.cc_emails || []);
      
      // Fetch asset if asset_id exists
      if (ticketRes.data.asset_id) {
        try {
          const assetRes = await api.get(`/agents/${ticketRes.data.asset_id}`);
          setAsset(assetRes.data);
        } catch (error) {
          setAsset(null);
        }
      }
      
      // Fetch client if client_id exists
      if (ticketRes.data.client_id) {
        try {
          const [clientRes, contactsRes] = await Promise.all([
            api.get(`/clients/${ticketRes.data.client_id}`),
            api.get(`/clients/${ticketRes.data.client_id}/contacts`)
          ]);
          // Attach contacts to client object
          setClient({
            ...clientRes.data,
            contacts: contactsRes.data || []
          });
        } catch (error) {
          setClient(null);
        }
      }
      
      // Fetch previous tickets (same client or asset)
      try {
        const ticketsRes = await api.get('/tickets');
        const relatedTickets = ticketsRes.data
          .filter(t => {
            // Exclude current ticket
            if (t.id === id) return false;
            // Include tickets from same client or asset
            return (
              (ticketRes.data.client_id && t.client_id === ticketRes.data.client_id) ||
              (ticketRes.data.asset_id && t.asset_id === ticketRes.data.asset_id)
            );
          })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 5); // Show last 5 tickets
        setPreviousTickets(relatedTickets);
      } catch (error) {
        setPreviousTickets([]);
      }
    } catch (error) {
      toast.error('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/tickets/${id}`, { status: newStatus });
      setTicket({ ...ticket, status: newStatus });
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      await api.put(`/tickets/${id}`, { priority: newPriority });
      setTicket({ ...ticket, priority: newPriority });
      toast.success('Priority updated successfully');
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      // Use the new reply endpoint that supports email notifications
      const response = await api.post(`/tickets/${id}/reply`, { 
        comment: newComment, 
        is_internal: isInternal,
        send_email: sendEmailOnReply && !isInternal  // Don't send email for internal notes
      });
      
      setNewComment('');
      setIsInternal(false);
      await fetchTicketData();
      
      if (response.data.email_sent) {
        toast.success('Reply added and email sent to requester');
      } else {
        toast.success(isInternal ? 'Internal note added' : 'Reply added');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickResolve = async () => {
    setResolving(true);
    try {
      await api.post(`/tickets/${id}/resolve`, { 
        send_email: false 
      });
      setTicket({ ...ticket, status: 'resolved' });
      toast.success('Ticket resolved');
    } catch (error) {
      toast.error('Failed to resolve ticket');
    } finally {
      setResolving(false);
    }
  };

  const handleContactChange = async (contactId) => {
    setSavingContact(true);
    try {
      await api.put(`/tickets/${id}`, { contact_id: contactId === 'none' ? null : contactId });
      setTicket({ ...ticket, contact_id: contactId === 'none' ? null : contactId });
      toast.success('Contact updated');
    } catch (error) {
      toast.error('Failed to update contact');
    } finally {
      setSavingContact(false);
    }
  };

  const handleAddCcEmail = async () => {
    if (!newCcEmail.trim()) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCcEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    const updatedCc = [...ccEmails, newCcEmail.trim()];
    try {
      await api.put(`/tickets/${id}`, { cc_emails: updatedCc });
      setCcEmails(updatedCc);
      setNewCcEmail('');
      toast.success('CC email added');
    } catch (error) {
      toast.error('Failed to add CC email');
    }
  };

  const handleRemoveCcEmail = async (emailToRemove) => {
    const updatedCc = ccEmails.filter(e => e !== emailToRemove);
    try {
      await api.put(`/tickets/${id}`, { cc_emails: updatedCc });
      setCcEmails(updatedCc);
      toast.success('CC email removed');
    } catch (error) {
      toast.error('Failed to remove CC email');
    }
  };

  const handleAddContactAsCc = (email) => {
    if (email && !ccEmails.includes(email)) {
      handleAddCcEmailDirect(email);
    }
  };

  const handleAddCcEmailDirect = async (email) => {
    const updatedCc = [...ccEmails, email];
    try {
      await api.put(`/tickets/${id}`, { cc_emails: updatedCc });
      setCcEmails(updatedCc);
      toast.success('CC email added');
    } catch (error) {
      toast.error('Failed to add CC email');
    }
  };

  const handleResolveWithEmail = async () => {
    setResolving(true);
    try {
      const response = await api.post(`/tickets/${id}/resolve`, { 
        resolution_note: resolutionNote,
        send_email: true 
      });
      setTicket({ ...ticket, status: 'resolved' });
      setShowResolveDialog(false);
      setResolutionNote('');
      
      if (response.data.email_sent) {
        toast.success('Ticket resolved and notification sent to requester');
      } else {
        toast.success('Ticket resolved (email not sent - check SMTP settings or requester email)');
      }
    } catch (error) {
      toast.error('Failed to resolve ticket');
    } finally {
      setResolving(false);
    }
  };

  const analyzeTicketWithAI = async () => {
    setAiAnalyzing(true);
    setShowAiDialog(true);
    setAiAnalysis(null);
    
    try {
      const res = await api.post('/ai/analyze-ticket', {
        title: ticket.title,
        description: ticket.description,
        priority: ticket.priority,
        client_name: ticket.client_name || 'Unknown Client'
      });
      
      setAiAnalysis(res.data.analysis);
    } catch (error) {
      console.error('AI ticket analysis error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'AI analysis failed';
      toast.error(errorMsg);
      setShowAiDialog(false);
    } finally {
      setAiAnalyzing(false);
    }
  };


  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(`/tickets/${id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchTicketData();
      toast.success('File uploaded successfully');
      event.target.value = ''; // Reset file input
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/tickets/${id}/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      await api.delete(`/tickets/${id}/attachments/${attachmentId}`);
      await fetchTicketData();
      toast.success('Attachment deleted successfully');
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDownloadScreenshots = async (minutes) => {
    if (!asset) {
      toast.error('Agent information not available');
      return;
    }

    try {
      toast.info(`Generating screenshot history for last ${minutes} minutes...`);
      
      const response = await api.get(`/agent/${asset.id}/screenshots/history?minutes=${minutes}`, {
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `screenshots_${minutes}min.zip`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Screenshot history downloaded: ${filename}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(`No screenshots found for the last ${minutes} minutes. Screenshot capture may be disabled or the agent hasn't captured any screenshots yet.`);
      } else {
        toast.error('Failed to download screenshot history');
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
      case 'closed': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-lg text-gray-600">Ticket not found</p>
          <Button onClick={() => navigate('/tickets')} className="mt-4">
            Back to Tickets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/tickets')}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{ticket.title}</h1>
              <p className="text-sm text-gray-600 mt-1">Ticket #{ticket.id.slice(0, 8)}</p>
            </div>
          </div>
          
          {/* Resolve Buttons */}
          {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleQuickResolve}
                disabled={resolving}
                className="gap-2 text-green-700 border-green-300 hover:bg-green-50"
              >
                {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Quick Resolve
              </Button>
              <Button
                onClick={() => setShowResolveDialog(true)}
                disabled={resolving}
                className="gap-2 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4" />
                Resolve
                <MailIcon className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Ticket Description */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Description</CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={analyzeTicketWithAI}
                    className="gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    AI Analysis
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div data-color-mode="light">
                  <MDEditor.Markdown source={ticket.description} className="prose prose-sm max-w-none" />
                </div>
              </CardContent>
            </Card>

            {/* Attachments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Paperclip className="w-5 h-5" />
                  Attachments ({attachments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {attachments.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No attachments yet</p>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((attachment) => (
                      <div key={attachment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                        <div className="flex items-center gap-3 flex-1">
                          <FileIcon className="w-5 h-5 text-gray-600" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{attachment.original_filename}</p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(attachment.file_size)} • {new Date(attachment.uploaded_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename)}
                            className="gap-1"
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            className="gap-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload File */}
                <div className="pt-4 border-t">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                  />
                  <label htmlFor="file-upload">
                    <Button
                      type="button"
                      variant="outline"
                      className="gap-2 w-full"
                      disabled={uploadingFile}
                      onClick={() => document.getElementById('file-upload').click()}
                    >
                      {uploadingFile ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Paperclip className="w-4 h-4" />
                          Upload File (Max 10MB)
                        </>
                      )}
                    </Button>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Screenshot History */}
            {asset && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="w-5 h-5" />
                    Screenshot History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    Download screenshots from the agent to see what was happening at the time of the issue.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[10, 20, 30].map(minutes => (
                      <Button
                        key={minutes}
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadScreenshots(minutes)}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Last {minutes} minutes
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Note: Screenshot history is only available if the agent has screenshot capture enabled (5-second intervals)
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Comments */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Comments ({comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filter Toggle */}
                <div className="flex items-center justify-between pb-2 border-b">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={showInternalNotes}
                      onCheckedChange={setShowInternalNotes}
                      id="show-internal"
                    />
                    <Label htmlFor="show-internal" className="text-sm cursor-pointer">
                      Show internal notes
                    </Label>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="bg-yellow-50">
                      <Lock className="w-3 h-3 mr-1" />
                      Internal
                    </Badge>
                    <Badge variant="outline">Public</Badge>
                  </div>
                </div>

                {comments.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No comments yet</p>
                ) : (
                  <div className="space-y-4">
                    {comments
                      .filter(comment => showInternalNotes || !comment.is_internal)
                      .map((comment) => (
                      <div 
                        key={comment.id} 
                        className={`border-l-4 p-4 rounded-r ${
                          comment.is_internal 
                            ? 'border-yellow-400 bg-yellow-50' 
                            : 'border-purple-400 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {comment.is_internal ? (
                              <Lock className="w-4 h-4 text-yellow-600" />
                            ) : (
                              <User className="w-4 h-4 text-gray-600" />
                            )}
                            <span className="font-semibold text-sm">{comment.user_name}</span>
                            {comment.is_internal && (
                              <Badge variant="outline" className="bg-yellow-100 text-yellow-800 text-xs">
                                Internal Note
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div data-color-mode="light">
                          <MDEditor.Markdown source={comment.comment} className="prose prose-sm max-w-none" />
                        </div>
                        {comment.is_internal && (
                          <p className="text-xs text-yellow-700 mt-2 italic">
                            🔒 This note is only visible to technicians and will not be emailed to the customer
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Comment */}
                <div className="pt-4 border-t space-y-3">
                  <div data-color-mode="light">
                    <MDEditor
                      value={newComment}
                      onChange={(value) => setNewComment(value || '')}
                      preview="edit"
                      height={150}
                      textareaProps={{
                        placeholder: isInternal 
                          ? 'Add an internal note (supports Markdown)...' 
                          : 'Add a reply (supports Markdown)...'
                      }}
                    />
                  </div>
                  
                  {/* Toggle Options */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={isInternal}
                          onCheckedChange={(checked) => {
                            setIsInternal(checked);
                            if (checked) setSendEmailOnReply(false);
                          }}
                          id="internal-note"
                        />
                        <Label htmlFor="internal-note" className="text-sm cursor-pointer">
                          Internal note
                        </Label>
                        {isInternal && <Lock className="w-4 h-4 text-yellow-600" />}
                      </div>
                      
                      {!isInternal && (
                        <div className="flex items-center gap-2 border-l pl-4">
                          <Switch
                            checked={sendEmailOnReply}
                            onCheckedChange={setSendEmailOnReply}
                            id="send-email"
                          />
                          <Label htmlFor="send-email" className="text-sm cursor-pointer flex items-center gap-1">
                            <MailIcon className="w-3 h-3" />
                            Email requester
                          </Label>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submitting}
                    className={`gap-2 ${isInternal ? 'bg-yellow-600 hover:bg-yellow-700' : ''}`}
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isInternal ? (
                      <Lock className="w-4 h-4" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {sendEmailOnReply && <MailIcon className="w-3 h-3" />}
                      </>
                    )}
                    {isInternal ? 'Add Internal Note' : (sendEmailOnReply ? 'Reply & Email' : 'Add Reply')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status & Priority */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Status & Priority</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
                    <Select value={ticket.status} onValueChange={handleStatusChange}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Priority</label>
                    <Select value={ticket.priority} onValueChange={handlePriorityChange}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Time Tracking */}
            <TicketTimeTracker ticketId={id} clientId={ticket.client_id} />

            {/* Requester Information */}
            {(ticket.requester_name || ticket.requester_email) && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Requester Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5 text-sm">
                  {ticket.requester_name && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs w-12">Name:</span>
                      <span className="font-medium">{ticket.requester_name}</span>
                    </div>
                  )}
                  {ticket.requester_email && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs w-12">Email:</span>
                      <a href={`mailto:${ticket.requester_email}`} className="font-medium text-blue-600 hover:underline break-all">
                        {ticket.requester_email}
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Client Information */}
            {client && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Client Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1.5 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs w-16">Company:</span>
                    <span className="font-medium">{client.company_name}</span>
                  </div>
                  
                  {/* Company Phone */}
                  {client.phone && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs w-16">Phone:</span>
                      <a href={`tel:${client.phone}`} className="font-medium text-blue-600 hover:underline">
                        {client.phone}
                      </a>
                    </div>
                  )}
                  
                  {/* Company Email */}
                  {client.email && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs w-16">Email:</span>
                      <a href={`mailto:${client.email}`} className="font-medium text-blue-600 hover:underline break-all">
                        {client.email}
                      </a>
                    </div>
                  )}
                  
                  {/* Assigned Contact/Requester */}
                  {ticket.contact_id && client.contacts && client.contacts.length > 0 && (() => {
                    const assignedContact = client.contacts.find(c => c.id === ticket.contact_id);
                    if (assignedContact) {
                      return (
                        <div className="pt-2 mt-2 border-t space-y-1">
                          <span className="text-gray-600 font-medium text-xs">Assigned Contact</span>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs w-16">Name:</span>
                            <span className="font-medium">{assignedContact.name}</span>
                          </div>
                          {assignedContact.email && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 text-xs w-16">Email:</span>
                              <a href={`mailto:${assignedContact.email}`} className="text-blue-600 hover:underline">
                                {assignedContact.email}
                              </a>
                            </div>
                          )}
                          {assignedContact.phone && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 text-xs w-16">Phone:</span>
                              <a href={`tel:${assignedContact.phone}`} className="text-blue-600 hover:underline">
                                {assignedContact.phone}
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    }
                  })()}
                  
                  {/* Primary Contact (if no assigned contact) */}
                  {!ticket.contact_id && client.contacts && client.contacts.length > 0 && (
                    <div className="pt-2 mt-2 border-t space-y-1">
                      <span className="text-gray-600 font-medium text-xs">Primary Contact</span>
                      {(() => {
                        const primary = client.contacts.find(c => c.is_primary) || client.contacts[0];
                        return (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 text-xs w-16">Name:</span>
                              <span className="font-medium">{primary.name}</span>
                            </div>
                            {primary.email && (
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 text-xs w-16">Email:</span>
                                <a href={`mailto:${primary.email}`} className="text-blue-600 hover:underline">
                                  {primary.email}
                                </a>
                              </div>
                            )}
                            {primary.phone && (
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500 text-xs w-16">Phone:</span>
                                <a href={`tel:${primary.phone}`} className="text-blue-600 hover:underline">
                                  {primary.phone}
                                </a>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2 w-full mt-2"
                    onClick={() => navigate(`/clients/${client.id}`)}
                  >
                    <LinkIcon className="w-3 h-3" />
                    View Client
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Asset Information */}
            {/* Related Asset */}
            {asset && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <HardDrive className="w-4 h-4" />
                    Related Asset
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm">
                  <div 
                    className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/assets-rmm/${asset.id}`)}
                  >
                    <Monitor className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-blue-900 truncate">{asset.hostname || 'Unknown Device'}</p>
                      {asset.ip_address && (
                        <p className="text-xs text-blue-700 font-mono">{asset.ip_address}</p>
                      )}
                    </div>
                    <Badge className={`flex-shrink-0 text-xs ${asset.status === 'online' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {asset.status === 'online' ? '🟢 Online' : '🔴 Offline'}
                    </Badge>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2 w-full mt-3"
                    onClick={() => navigate(`/assets-rmm/${asset.id}`)}
                  >
                    <LinkIcon className="w-3 h-3" />
                    View Asset Details
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Additional Details */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Additional Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs w-20">Created:</span>
                  <span className="font-medium text-xs">{new Date(ticket.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs w-20">Updated:</span>
                  <span className="font-medium text-xs">{new Date(ticket.updated_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs w-20">Category:</span>
                  <Badge variant="outline" className="text-xs">{ticket.category || 'general'}</Badge>
                </div>
                {ticket.reported_by && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs w-20">Reported:</span>
                    <span className="font-medium">{ticket.reported_by}</span>
                  </div>
                )}
                {ticket.assigned_to && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs w-20">Assigned:</span>
                    <span className="font-medium">{ticket.assigned_to}</span>
                  </div>
                )}
                
                {/* Contact Dropdown */}
                {client && client.contacts && client.contacts.length > 0 && (
                  <div className="pt-2 border-t">
                    <label className="text-gray-500 text-xs block mb-1">Primary Contact</label>
                    <Select
                      value={ticket.contact_id || 'none'}
                      onValueChange={handleContactChange}
                      disabled={savingContact}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Select contact" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No contact selected</SelectItem>
                        {client.contacts.map((contact) => (
                          <SelectItem key={contact.id} value={contact.id}>
                            {contact.name} {contact.email ? `(${contact.email})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* CC Emails Section */}
                <div className="pt-2 border-t">
                  <label className="text-gray-500 text-xs block mb-2">CC Recipients</label>
                  
                  {/* Existing CC emails */}
                  {ccEmails.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {ccEmails.map((email, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary" 
                          className="text-xs gap-1 pr-1"
                        >
                          {email}
                          <button
                            onClick={() => handleRemoveCcEmail(email)}
                            className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Add from client contacts */}
                  {client && client.contacts && client.contacts.length > 0 && (
                    <Select
                      value=""
                      onValueChange={(value) => {
                        const contact = client.contacts.find(c => c.id === value);
                        if (contact && contact.email) {
                          handleAddContactAsCc(contact.email);
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs mb-2">
                        <SelectValue placeholder="Add from contacts..." />
                      </SelectTrigger>
                      <SelectContent>
                        {client.contacts
                          .filter(c => c.email && !ccEmails.includes(c.email))
                          .map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.name} ({contact.email})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {/* Manual email entry */}
                  <div className="flex gap-1">
                    <Input
                      type="email"
                      placeholder="Add email address..."
                      value={newCcEmail}
                      onChange={(e) => setNewCcEmail(e.target.value)}
                      className="h-8 text-xs flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCcEmail();
                        }
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 px-2"
                      onClick={handleAddCcEmail}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Previous Tickets */}
            {previousTickets.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Previous Tickets ({previousTickets.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {previousTickets.map((prevTicket) => (
                    <div
                      key={prevTicket.id}
                      onClick={() => navigate(`/tickets/${prevTicket.id}`)}
                      className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="text-sm font-medium line-clamp-2 flex-1">
                          {prevTicket.title}
                        </h4>
                        <Badge 
                          variant="outline" 
                          className={`text-xs flex-shrink-0 ${
                            prevTicket.status === 'closed' ? 'bg-gray-100' : 
                            prevTicket.status === 'open' ? 'bg-blue-50' : 
                            'bg-yellow-50'
                          }`}
                        >
                          {prevTicket.status}
                        </Badge>
                      </div>
                      {prevTicket.asset_id && asset && (
                        <div className="flex items-center gap-1 text-xs text-gray-600 mb-1">
                          <Monitor className="w-3 h-3" />
                          <span className="truncate">{asset.hostname || 'Asset'}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(prevTicket.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                  {previousTickets.length === 5 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-xs"
                      onClick={() => navigate('/tickets')}
                    >
                      View all tickets →
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* AI Analysis Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Ticket Analysis
            </DialogTitle>
            <DialogDescription>
              AI-powered analysis and recommendations for this support ticket
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {aiAnalyzing ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-purple-600 mb-4" />
                <span className="text-gray-600">Analyzing ticket...</span>
                <span className="text-sm text-gray-400 mt-2">This may take a few seconds</span>
              </div>
            ) : aiAnalysis ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border border-purple-100">
                  <div className="prose prose-sm max-w-none">
                    <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {aiAnalysis}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowAiDialog(false)}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(aiAnalysis);
                      toast.success('Analysis copied to clipboard');
                    }}
                    className="gap-2"
                  >
                    Copy to Clipboard
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No analysis available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Resolve with Email Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Resolve Ticket
            </DialogTitle>
            <DialogDescription>
              Mark this ticket as resolved and notify the requester via email
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="resolution-note">Resolution Note (optional)</Label>
              <Textarea
                id="resolution-note"
                placeholder="Describe how the issue was resolved..."
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-gray-500">
                This note will be included in the email to the requester and saved as an internal note.
              </p>
            </div>
            
            {ticket?.requester_email ? (
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <MailIcon className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-blue-800">
                  Email will be sent to: <strong>{ticket.requester_email}</strong>
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <AlertCircle className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  No requester email found. Ticket will be resolved without notification.
                </span>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowResolveDialog(false);
                setResolutionNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResolveWithEmail}
              disabled={resolving}
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              {resolving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Resolve
                  <MailIcon className="w-4 h-4" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default TicketDetail;
